import "./RecentMissionsSearch.css";
import { React, useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../Security/AuthProvider";
import { useNotification } from '../../../Utils/Commons/Notification/NotificationContext';

// Fonction pour convertir le format français DD/MM/YYYY vers YYYY-MM-DD pour l'API
const convertDateForAPI = (frenchDate) => {
  if (!frenchDate || !frenchDate.includes('/')) return undefined;
  const [day, month, year] = frenchDate.split('/');
  return `${year}-${month}-${day}`;
};

const RecentMissionsSearch = ({
  setRecentMissions,
  setIsLoading,
  isLoading,
}) => {
  const [clientName, setClientName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [operationName, setOperationName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showAllMissions, setShowAllMissions] = useState(false);
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();

  const fetchFilteredRecentMissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        company_name: getCredentials().company_name,
        company_password: getCredentials().company_password,
        clientName,
        agentName,
        operationName,
        startDate: convertDateForAPI(startDate), // Convertir le format français vers API
        endDate: convertDateForAPI(endDate), // Convertir le format français vers API
      };

      // Choisir la route appropriée selon si l'utilisateur veut toutes les missions ou non
      const endpoint = showAllMissions ? "/recent-missions/get-all-unlimited" : "/recent-missions/get-all";
      
      // Si on utilise la route normale, on peut toujours ajouter limit pour plus de résultats
      if (!showAllMissions) {
        requestBody.limit = 50; // Plus que les 20 par défaut, mais pas toutes
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (data.error) {
        notifyError(data.error);
        setRecentMissions([]);
        return;
      }
      
      // Gestion de la compatibilité avec l'ancien ET le nouveau format de serveur
      let missions = [];
      if (Array.isArray(data)) {
        // Format ancien : directement un tableau
        missions = data;
      } else if (data.success && Array.isArray(data.data)) {
        // Format nouveau optimisé : { success: true, data: [...] }
        missions = data.data;
      } else if (data.data && Array.isArray(data.data)) {
        // Format alternatif : { data: [...] }
        missions = data.data;
      } else {
        // Fallback : essayer de trouver un tableau dans les propriétés
        missions = [];
        console.warn("Format de données non reconnu:", data);
      }
      
      setRecentMissions(missions);
      
      // Afficher des informations utiles sur la récupération (sans les dates)
      if (data.totalCount !== undefined) {
        if (showAllMissions) {
          notifySuccess(`Toutes les missions récupérées : ${missions.length} mission(s)`);
        } else {
          notifySuccess(`${missions.length} mission(s) trouvée(s) sur ${data.totalCount} au total`);
        }
      } else if (data.pagination && data.pagination.totalCount !== undefined) {
        notifySuccess(`${missions.length} mission(s) trouvée(s) sur ${data.pagination.totalCount} au total`);
      } else {
        notifySuccess(`${missions.length} mission(s) trouvée(s)`);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des missions:", error);
      notifyError("Failed to fetch recent missions. Please try again.");
      setRecentMissions([]); // Assurer un tableau vide en cas d'erreur
    } finally {
      setIsLoading(false);
    }
  }, [clientName, agentName, operationName, startDate, endDate, showAllMissions, getCredentials, notifyError, notifySuccess, setRecentMissions, setIsLoading]);

  // Initialiser les dates par défaut (aujourd'hui et il y a 2 semaines) - SANS lancer de recherche
  useEffect(() => {
    const today = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(today.getDate() - 14);

    // Formater les dates au format DD/MM/YYYY pour l'affichage français
    const formatDateFR = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    setEndDate(formatDateFR(today));
    setStartDate(formatDateFR(twoWeeksAgo));
  }, []);

  const handleSearch = () => {
    fetchFilteredRecentMissions();
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setClientName("");
    setAgentName("");
    setOperationName("");
    
    // Réinitialiser les dates aux valeurs par défaut
    const today = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(today.getDate() - 14);

    const formatDateFR = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    setEndDate(formatDateFR(today));
    setStartDate(formatDateFR(twoWeeksAgo));
    setShowAllMissions(false);
  };

  return (
    <div className="recent-missions-search-bar">
      <h2>Rechercher des missions récentes</h2>
      <div className="recent-missions-search-inputs">
        <input
          type="text"
          placeholder="Nom du client"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />
        <input
          type="text"
          placeholder="Nom de l'agent"
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
        />
        <input
          type="text"
          placeholder="Nom de l'opération"
          value={operationName}
          onChange={(e) => setOperationName(e.target.value)}
        />
        <div className="date-filters">
          <input
            type="text"
            placeholder="Date de début (JJ/MM/AAAA)"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            title="Format: JJ/MM/AAAA"
          />
          <input
            type="text"
            placeholder="Date de fin (JJ/MM/AAAA)"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            title="Format: JJ/MM/AAAA"
          />
        </div>
        <button
          onClick={() => setShowAllMissions(!showAllMissions)}
          className={`toggle-button ${showAllMissions ? 'active' : ''}`}
          disabled={isLoading}
        >
          Voir toutes les missions
        </button>
        <div className="search-buttons">
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="search-button"
          >
            {isLoading ? "Chargement..." : "Rechercher"}
          </button>
          <button
            onClick={resetFilters}
            disabled={isLoading}
            className="reset-button"
          >
            Réinitialiser
          </button>
        </div>
      </div>
      {showAllMissions && (
        <div style={{ 
          marginTop: '10px', 
          padding: '10px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#856404'
        }}>
          ⚠️ Attention : Cette option peut prendre plus de temps si vous avez beaucoup de missions.
        </div>
      )}
    </div>
  );
};

export default RecentMissionsSearch;
